import { Link, useLocation } from "react-router-dom";
import CreditListPerSong from "./CreditListPerSong";
import React, {
  Key,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  ReactPortal,
  useState,
  useEffect
} from "react";
// import ShareIcon from "../images/arrow-up-from-bracket-solid.svg?react";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { encodeId } from "./HelperMethods";
import { Song, Credits, ArtistInfo, AlbumInfo, SongDetailsProps } from "../types";
import { authFetch, isAuthenticated } from "../utils/authUtils";
import StarRating from "./StarRating";
import "./StarRating.css";
import ReviewsContainer from "./Review/ReviewsContainer";

interface RatingSummary {
  rating_count: number;
  average_rating: number;
}

interface UserReview {
  id: string;
  rating: number;
  content: string;
  privacy: string;
  _previousRating?: number;
}

function showSongHeader(song: Song, featured: Credits[], artistinfo: ArtistInfo[], albuminfo: AlbumInfo, type: string) {
  const album_id = song.melon_album_id;

  return (
    <div className={"song-header" + type}>
      <h5 className="album-name-song-page">
        {song.spotify_song_id &&
          <a href={`https://open.spotify.com/track/${song.spotify_song_id}`} target="_blank" rel="noopener noreferrer">
            <img src="/spotify_logo.png" alt="Listen on Spotify" style={{ width: "16px", height: "16px", boxShadow: "none" }} />
          </a>
        }
        TRACK {song.track_order} ON{" "}
        <Link to={`/album/${encodeId(album_id)}`} state={{ id: album_id }} className="a-name-link">
          {albuminfo.album_english_name.toUpperCase()}
        </Link>{" "}

      </h5>
      <div className="main-song-text">
        <h1 className="song-name">
          {song.is_title && "✰"}
          {song.is_explicit && "🅴 "} {song.english_name}
          {song.korean_name && <div className="korean-small-text"> {song.korean_name && " (" + song.korean_name + ")"}</div>}
        </h1>
      </div>
      <div className="artist-credits-list">
        <div className="main-performer-list">
          by{artistinfo &&
            artistinfo.map((name) => (
              <h4 className="artist-credit-link" key={name.artist_id}>
                <Link
                  to={`/artist/${encodeId(name.artist_id)}`}
                  state={{ id: name.artist_id }}
                >
                  {name.artist_id != -1 && name.artist_english_name}
                </Link>
                {name.artist_id == -1 && name.artist_english_name}
              </h4>
            ))}
        </div>
        {featured.length > 0 && (
          <div className="featured-list">
            featuring
            {featured.map((credit) => (
              <p className="credits-list-inner" key={credit._id}>
                <Link
                  to={`/artist/${encodeId(credit.artist_id)}`}
                  state={{ id: credit.artist_id }}
                >
                  {credit.english_name}
                </Link>
              </p>
            ))}
          </div>
        )}
      </div>
      <h5 className="album-name-song-page-mobile" style={{ "marginBottom": "0em" }}>
        {song.spotify_song_id &&
          <a href={`https://open.spotify.com/track/${song.spotify_song_id}`} target="_blank" rel="noopener noreferrer">
            <img src="/spotify_logo.png" alt="Listen on Spotify" style={{ width: "16px", height: "16px", boxShadow: "none" }} />
          </a>
        }
        TRACK {song.track_order} ON{" "}
        <Link to={`/album/${encodeId(album_id)}`} state={{ id: album_id }} className="album-link-mobile">
          {albuminfo.album_english_name.toUpperCase()}
        </Link>
      </h5>
    </div>);
}


const SongDetails: React.FC<SongDetailsProps> = ({
  song,
  credits,
  artistinfo,
  albuminfo,
}) => {
  const album_id = song.melon_album_id;
  const featured = credits.filter((c) => c.credit_type.includes("F"));
  const written = credits.filter((c) => c.credit_type.includes("L"));
  const composed = credits.filter((c) => c.credit_type.includes("C"));
  const arranged = credits.filter((c) => c.credit_type.includes("A"));
  const [ratingSummary, setRatingSummary] = useState<RatingSummary | null>(null);
  const [ratingsRefreshCounter, setRatingsRefreshCounter] = useState(0);
  const [userReview, setUserReview] = useState<UserReview | null>(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState<boolean>(false);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const location = useLocation();

  var picture_url;
  if (albuminfo.s3_picture_url) {
    picture_url = albuminfo.s3_picture_url;
  } else {
    picture_url = "";
  }
  const tooltip = <Tooltip id="tooltip">copy song link</Tooltip>;
  const copied_tooltip = <Tooltip id="tooltip">copied song url!</Tooltip>;
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const copyLink = () => {
    // Your copy link logic here
    navigator.clipboard.writeText(window.location.href);
    setShowCopiedTooltip(true);
    setTimeout(() => {
      setShowCopiedTooltip(false);
    }, 1000); // Tooltip will disappear after 1 second
  };

  // Check if user is logged in
  useEffect(() => {
    setIsUserLoggedIn(isAuthenticated());
  }, []);

  // Listen for custom event when reviews change
  useEffect(() => {
    const refreshRatings = () => {
      setRatingsRefreshCounter(prev => prev + 1);
    };

    // Add event listener for review changes
    window.addEventListener('reviewUpdated', refreshRatings);

    return () => {
      // Clean up event listener
      window.removeEventListener('reviewUpdated', refreshRatings);
    };
  }, []);

  // Get the current user ID
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return null;
      return JSON.parse(userData);
    } catch (e) {
      console.error("Error parsing user data:", e);
      return null;
    }
  };

  // Fetch user's own review if they're logged in
  useEffect(() => {
    const fetchUserReview = async () => {
      if (!isUserLoggedIn) {
        console.log("User not logged in, skipping review fetch");
        return;
      }

      try {
        // Check if we have a valid token before making the request
        const token = localStorage.getItem('token');
        if (!token) {
          console.log("No auth token found, user may be logged out");
          setIsUserLoggedIn(false);
          return;
        }

        // Try a different approach - get all reviews for the song 
        // and filter for the user's review client-side
        console.log("Fetching all reviews for song ID:", song.melon_song_id);

        // This endpoint should be more reliable as it's used elsewhere in the app
        const response = await fetch(`/api/reviews/song/${song.melon_song_id}`);

        if (!response.ok) {
          console.error("Error fetching reviews:", response.status);
          return;
        }

        // Get the current user data
        const userData = getUserData();
        // Try different possible user ID field names
        const userId = userData?.userId || userData?.id || userData?.user_id;

        console.log("Current user data:", userData);
        console.log("Extracted user ID:", userId);

        if (!userId) {
          console.log("No user ID found in user data");
          return;
        }

        // Parse the reviews and find the user's review
        const reviews = await response.json();
        console.log("Found", reviews.length, "reviews for this song");

        // Find the user's review in the list - try different possible ID field names
        const userReviewFromList = reviews.find((review: any) =>
          review.user_id === userId ||
          review.userId === userId ||
          review.is_owner === true
        );

        if (userReviewFromList) {
          console.log("Found user's review:", userReviewFromList);
          setUserReview(userReviewFromList);
        } else {
          console.log("User hasn't reviewed this song yet");
          setUserReview(null);
        }
      } catch (err) {
        console.error("Error fetching user review:", err);
        setUserReview(null);
      }
    };

    fetchUserReview();
  }, [song.melon_song_id, isUserLoggedIn, ratingsRefreshCounter]);

  useEffect(() => {
    const fetchRatingSummary = async () => {
      try {
        const response = await fetch(`/api/reviews/song/${song.melon_song_id}/rating`);
        if (response.ok) {
          const data = await response.json();
          setRatingSummary(data);
        }
      } catch (err) {
        console.error("Error fetching rating summary:", err);
        // Don't set an error for this, as it's not critical
      }
    };

    fetchRatingSummary();
  }, [song.melon_song_id, ratingsRefreshCounter]);

  const handleQuickRate = async (rating: number) => {
    if (!isUserLoggedIn || isSubmittingRating) return;

    console.log("Attempting to rate song with rating:", rating);
    console.log("Current user review state:", userReview);
    console.log("Song ID being used:", song.melon_song_id);

    setIsSubmittingRating(true);

    // Store the previous rating before updating
    const previousRating = userReview ? userReview.rating : 0;

    // Immediately update the UI with the new rating for instant feedback
    const optimisticReview = userReview
      ? { ...userReview, rating, _previousRating: previousRating }
      : { id: 'temp-id', rating, content: '', privacy: 'public', _previousRating: 0 };

    setUserReview(optimisticReview);

    // Also trigger a rating refresh immediately to update UI everywhere
    setRatingsRefreshCounter(prev => prev + 1);

    // Dispatch custom event to notify that a review was updated for the reviews container at the bottom
    window.dispatchEvent(new CustomEvent('reviewUpdated'));

    // Dispatch an additional event specifically for the reviews list section
    window.dispatchEvent(new CustomEvent('reviewsListUpdated', {
      detail: {
        songId: song.melon_song_id,
        rating: rating
      }
    }));

    try {
      let response;
      const userData = getUserData();
      const userId = userData?.userId || userData?.id || userData?.user_id;

      console.log("User ID for review:", userId);

      if (userReview) {
        // Update existing review
        console.log("Updating existing review with ID:", userReview.id);

        const updateData = {
          rating: rating,
          content: userReview.content || " ",
          privacy: userReview.privacy || "public"
        };

        console.log("Update data being sent:", updateData);

        response = await authFetch(`/api/reviews/${userReview.id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(updateData)
        });
      } else {
        // Create new review with just a rating
        console.log("Creating new review for song ID:", song.melon_song_id);

        // Create a formData object that matches the format used in ReviewForm.tsx
        const formData = {
          rating: rating,
          content: " ", // Using a text description as content
          privacy: "public" // Adding privacy field explicitly
        };

        const requestBody = {
          song_id: song.melon_song_id,
          ...formData
        };

        // Log the exact request body being sent
        console.log("Sending review creation request with body:", JSON.stringify(requestBody));

        // Same format as used in ReviewForm.tsx
        response = await authFetch("/api/reviews", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestBody)
        });
      }

      console.log("API response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("Review saved successfully:", data);

        // Update the user review with the newly created one
        setUserReview({
          id: data.id,
          rating: data.rating,
          content: data.content || '',
          privacy: data.privacy,
          _previousRating: userReview?.rating
        });

        // Dispatch custom event to notify that a review was added or updated
        window.dispatchEvent(new CustomEvent('reviewUpdated'));
      } else {
        // Log full error details
        const errorText = await response.text();
        console.error("Error response from server:", errorText);

        // If there was an error, revert to the previous state
        if (userReview) {
          setUserReview(userReview);
        } else {
          setUserReview(null);
        }

        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error("Error in handleQuickRate:", error);
      // If we have response details from the error, log them
      if (error instanceof Error) {
        console.error("Error message:", error.message);
      }

      // Restore original state on error
      if (userReview) {
        setUserReview(userReview);
      } else {
        setUserReview(null);
      }
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Replace the renderStars function with the StarRating component
  const renderStars = (rating: number) => {
    return <StarRating rating={rating} />;
  };

  // Use the StarRating component for interactive stars
  const renderInteractiveStars = () => {
    // If we received userReview, use that rating. Otherwise use 0 (no stars)
    // We need to make sure to convert to a number, as the rating could come in different formats
    const currentRating = userReview ?
      (typeof userReview.rating === 'string' ? parseFloat(userReview.rating) : userReview.rating) : 0;

    return (
      <StarRating
        rating={currentRating}
        interactive={true}
        onRatingChange={handleQuickRate}
      />
    );
  };

  // Update the average rating display immediately when user rates
  useEffect(() => {
    if (!ratingSummary || !userReview) return;

    // Create an optimistic rating summary that includes the user's new rating
    const oldUserRating = userReview._previousRating; // This may be undefined for new reviews
    const newUserRating = userReview.rating;

    // Only do optimistic update if we have previous rating information
    if (oldUserRating !== undefined && oldUserRating !== newUserRating) {
      console.log("Calculating optimistic rating update");
      console.log("Previous rating:", oldUserRating);
      console.log("New rating:", newUserRating);

      const totalRatings = ratingSummary.rating_count;
      const currentSum = ratingSummary.average_rating * totalRatings;

      // If it's a new rating, add it to the total
      if (oldUserRating === 0) {
        const newSum = currentSum + newUserRating;
        const newCount = totalRatings + 1;
        const newAverage = newSum / newCount;

        console.log("Optimistic rating (new review):", newAverage, "count:", newCount);

        // Update with optimistic values
        setRatingSummary({
          rating_count: newCount,
          average_rating: newAverage
        });
      }
      // If it's updating an existing rating, adjust the average
      else {
        const adjustedSum = currentSum - oldUserRating + newUserRating;
        const newAverage = adjustedSum / totalRatings;

        console.log("Optimistic rating (update):", newAverage);

        // Update with optimistic values
        setRatingSummary({
          rating_count: totalRatings,
          average_rating: newAverage
        });
      }
    }
  }, [userReview]);

  // Function to handle login redirect with return URL
  const handleLoginRedirect = () => {
    localStorage.setItem('returnToUrl', location.pathname + location.search);
  };

  return (
    <div className="song-details">
      {showSongHeader(song, featured, artistinfo, albuminfo, "")}
      <div className="song-rating-summary">
        <div className="rating-container">
          {ratingSummary && ratingSummary.rating_count > 0 ? (
            <div className="avg-summary">
              <div className="rating-info">
                <div className="rating-type">users rated</div>
                <div className="rating-number">
                  <strong>{ratingSummary.average_rating.toFixed(1)}</strong>
                  <div className="number-of-ratings">({ratingSummary.rating_count})</div>
                </div>

                <div className="rating-stars">
                  {renderStars(ratingSummary.average_rating)}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-ratings">No ratings yet</div>
          )}

          <div className="user-rating">
            {isUserLoggedIn ? (
              <div className="my-rating">
                <div className="rating-info">
                  <div className="rating-type">my rating</div>
                  <div className="rating-number">
                    <strong>
                      {userReview ?
                        (typeof userReview.rating === 'number'
                          ? userReview.rating.toFixed(1)
                          : Number(userReview.rating).toFixed(1))
                        : "-"}
                    </strong>
                  </div>
                  <div className="rating-stars interactive-stars">
                    {renderInteractiveStars()}
                  </div>
                  {userReview === null && <div className="rating-prompt">Click to rate</div>}
                </div>
              </div>
            ) : (
              <div className="login-to-rate">
                <Link to="/login" className="login-button" onClick={handleLoginRedirect}>Log in to rate</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="song-cards">
        <div className="song-left-card">
          {/* <h5>RELEASED ON{" "}
            {albuminfo.release_date &&
              albuminfo.release_date.toString().slice(0, 10)}</h5> */}
          <div className="artist-photo-wrapper">
            <Link to={`/album/${encodeId(album_id)}`} state={{ id: album_id }}>
              <img
                className="main-photo"
                src={picture_url}
                alt={"album photo of " + albuminfo.album_english_name}
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  event.currentTarget.src = "/dubuFace.jpg";
                  event.currentTarget.onerror = null;
                }}
              />
            </Link>
          </div>

          {showSongHeader(song, featured, artistinfo, albuminfo, "-mobile")}

          <div className="all-info">
            {song.logged_at && (
              <i>
                data last updated on {song.logged_at.toString().slice(0, 10)}.
              </i>
            )}
          </div>
        </div>
        <div className="song-right-card">
          <div className="song-credits">
            {written.length > 0 && (
              <div className="artist-credit-list">
                <p style={{ "fontSize": "1.1em", "fontWeight": "600" }}>LYRICS</p><hr></hr>
                <CreditListPerSong credits={written} />
              </div>)}
            {composed.length > 0 && (
              <div className="artist-credit-list">
                <p style={{ "fontSize": "1.1em", "fontWeight": "600" }}>
                  COMPOSITION
                </p><hr></hr>
                <CreditListPerSong credits={composed} />
              </div>)}
            {arranged.length > 0 && (
              <div className="artist-credit-list">
                <p style={{ "fontSize": "1.1em", "fontWeight": "600" }}>
                  {" "}
                  ARRANGEMENT
                </p><hr></hr>
                <CreditListPerSong credits={arranged} />
              </div>
            )}
          </div>
        </div>
      </div>
      <ReviewsContainer songId={song.melon_song_id} />
    </div >
  );
};

export default SongDetails;
